/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import isNil from 'lodash.isnil';
import unionBy from 'lodash.unionby';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import Divider from '../../../components/divider/Divider';
import {LayerSettingsHeader} from '../../../maps/layers/LayerSettings';
import Details from './Details';
import {LayerProps} from './types';

/**
 * @private
 *
 * @method getSubLayerVisibility
 */
const getSubLayerVisibility = ({title, layers}: LayerProps) => {
  const visibility = [];
  layers?.forEach((item) => {
    visibility?.push({
      layerTitle : title,
      sublayerId : item.id,
      visibility : item.defaultVisibility || item.visibility
    });
  });
  return visibility;
};

/**
 * The layer provides a component which allows to display
 * accordion of layers
 *
 * ```typescript
 *
 *    <Layer layer={layer} />
 *
 * ```
 */
const Layer = ({
  layer,
  classes,
  onLayerVisibilityChange,
  onToggle,
  onToggleExpand,
  expandedLayer,
  changeOpacity,
  onSublayerVisibilityChange
}: LayerProps) => {
  const {layers, visibility: visible, title: layerTitle} = layer;
  const [selected, setSelected]     = useState(visible);
  const [activeTab, setActiveTab]   = useState([]);
  const sublayerVisibility          = getSubLayerVisibility(layer);
  const opacity                     = !isNil(layer?.opacity) ? layer?.opacity : 1;
  const hasSublayers                = Boolean(layers?.length);

  useEffect(() => {
    setSelected(visible);
    onLayerVisibilityChange({layerTitle, visible});
    onToggle(layerTitle, visible, layerTitle);
  }, [opacity, visible, layers, layerTitle]);

  /**
 * @private
 */
  const updateState = useCallback(current => {
    const updated = activeTab?.length ? unionBy(current, activeTab, 'layer') : current;
    setActiveTab(updated);
  }, [activeTab]);

  /**
 * @private
 */
  const isActiveTab = useCallback((current): Boolean => {
    if (activeTab?.length) {
      return activeTab?.findIndex(({layer, option}) => {
        return layer === current?.layer && option === current?.option;
      }) > -1;
    }
    return false;
  }, [activeTab]);

  const onToggleLayer = ({target:{checked, defaultValue}}, layerTitle) => {
    setSelected(checked);
    onLayerVisibilityChange({layerTitle, visible: checked});
    onToggle(defaultValue, checked, defaultValue);
  };

  return (
    <div className={classes.menuListItem}>
      <Divider />
      <Grid container direction='row' style={{padding: '0px 0 0px 0'}}>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={layerTitle}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={<LayerSettingsHeader label={layerTitle}
                isSelected={selected}
                onToggle={function(e) { onToggleLayer(e, layerTitle); }}/>}
              details={
                <Details layer={layer}
                  hasSublayers={hasSublayers}
                  sublayerVisibility={sublayerVisibility}
                  opacity={opacity}
                  classes={classes}
                  updateState={updateState}
                  isActiveTab={isActiveTab}
                  activeTab={activeTab}
                  onSublayerVisibilityChange={onSublayerVisibilityChange}
                  changeOpacity={changeOpacity}/>}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Layer;
